import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidationErrors,
} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import * as _ from 'lodash';
import { WorkflowStageValidationModel } from '@app/core/models/workflows/workflow-stage-validation.model';

@Component({
  selector: 'app-workflow-stage-validation',
  templateUrl: './workflow-stage-validation.component.html',
  styleUrls: ['./workflow-stage-validation.component.scss'],
})
export class WorkflowStageValidationComponent implements OnInit, OnDestroy {
  unsubscribe$: Subject<boolean> = new Subject<boolean>();

  workflowStageValidationForm: UntypedFormGroup;
  workflowStageLevel: number;
  workflowName: string;
  objectId: string;
  objectsId: string[];
  objectNumber: string;
  objectTypeName: string;
  submitted: boolean;
  isWFPromotion: boolean;
  isModifiedInvoice: boolean;
  approveWorkflowStage: (
    id: string,
    approval: WorkflowStageValidationModel
  ) => Observable<any>;
  approveWorkflowsStage: (
    ids: string[],
    approval: WorkflowStageValidationModel
  ) => Observable<any>;

  constructor(
    private dialogRef: MatDialogRef<WorkflowStageValidationComponent>,
    private formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) data
  ) {
    const level: number = Number(data.WorkflowStageLevel || 1);

    this.objectId = data.ObjectId;
    this.objectsId = data.ObjectsId;
    this.objectNumber = data.ObjectNumber;
    this.workflowName = data.WorkflowName;
    this.workflowStageLevel = level;
    this.objectTypeName = data.ObjectTypeName;
    this.isWFPromotion = data.IsWFPromotion;
    this.isModifiedInvoice = data.IsModifiedInvoice;
    this.approveWorkflowStage = data.ApproveWorkflowStage;
    this.approveWorkflowsStage = data.ApproveWorkflowsStage;
  }

  get IsFormValid(): boolean {
    return this.workflowStageValidationForm.valid;
  }

  ngOnInit(): void {
    this.buildFormControls();
  }

  buildFormControls(): void {
    if (!this.isWFPromotion) {
      this.workflowStageValidationForm = this.formBuilder.group(
        {
          IsValid: new UntypedFormControl({ options: [true, false] }, []),
          Comment: new UntypedFormControl({ value: ' ' }, []),
        },
        { validator: this.requiredIfIsNotValidValidator() }
      );
    } else {
      this.workflowStageValidationForm = this.formBuilder.group(
        {
          IsValidPromotion: new UntypedFormControl({ value: '' }, []),
          Comment: new UntypedFormControl({ value: ' ' }, []),
        },
        { validator: this.requiredIfIsNotValidValidatorPromotion() }
      );
    }

    this.workflowStageValidationForm.patchValue({ Comment: '' });
  }

  requiredIfIsNotValidValidator =
    () =>
    (group: UntypedFormGroup): ValidationErrors | null => {
      const isValidFilled: boolean =
        group &&
        group.controls.IsValid &&
        !_.isObject(group.controls.IsValid.value);
      const isValidValue: boolean =
        group && group.controls.IsValid && group.controls.IsValid.value == true;
      const isCommentFilled: boolean =
        group &&
        _.isString(group.controls.Comment.value) &&
        group.controls.Comment.value.trim().length > 0;

      return isValidFilled &&
        (isValidValue || (!isValidValue && isCommentFilled))
        ? null
        : {
            requiredIfIsNotValid: true,
          };
    };

  requiredIfIsNotValidValidatorPromotion =
    () =>
    (group: UntypedFormGroup): ValidationErrors | null => {
      const isValidFilled: boolean =
        group &&
        group.controls.IsValidPromotion &&
        !_.isObject(group.controls.IsValidPromotion.value);
      const isValidValue: boolean =
        group &&
        group.controls.IsValidPromotion &&
        group.controls.IsValidPromotion.value === '1';
      const isCommentFilled: boolean =
        group &&
        _.isString(group.controls.Comment.value) &&
        group.controls.Comment.value.trim().length > 0;

      return isValidFilled &&
        (isValidValue || (!isValidValue && isCommentFilled))
        ? null
        : {
            requiredIfIsNotValid: true,
          };
    };

  onSubmit = () => {
    if (this.IsFormValid) {
      if (_.isNil(this.objectsId) || !this.objectsId.length) {
        this.approveWorkflowCurrentStage();
        return;
      }

      this.approveWorkflowCurrentStages();
    }
  };

  close(isSuccess: boolean) {
    this.dialogRef.close(isSuccess);
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }

  approveWorkflowCurrentStage() {
    const workflowStageValidationModel = new WorkflowStageValidationModel();
    workflowStageValidationModel.IsValid = !this.isWFPromotion
      ? this.workflowStageValidationForm.controls.IsValid.value == true
      : null;
    workflowStageValidationModel.IsValidPromotion = this.isWFPromotion
      ? this.workflowStageValidationForm.controls.IsValidPromotion.value
      : null;
    workflowStageValidationModel.Comment =
      this.workflowStageValidationForm.controls.Comment.value;

    workflowStageValidationModel.IsModified = this.isModifiedInvoice;

    return this.approveWorkflowStage(
      this.objectId,
      workflowStageValidationModel
    )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        () => this.close(true)
      );
  }

  approveWorkflowCurrentStages() {
    const workflowStageValidationModel = new WorkflowStageValidationModel();
    workflowStageValidationModel.IsValid = !this.isWFPromotion
      ? this.workflowStageValidationForm.controls.IsValid.value == true
      : null;
    workflowStageValidationModel.IsValidPromotion = this.isWFPromotion
      ? this.workflowStageValidationForm.controls.IsValidPromotion.value
      : null;
    workflowStageValidationModel.Comment =
      this.workflowStageValidationForm.controls.Comment.value;

    workflowStageValidationModel.IsModified = false;

    return this.approveWorkflowsStage(
      this.objectsId,
      workflowStageValidationModel
    )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        () => this.close(true),
        () => this.close(false)
      );
  }
}
